import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { format, parseISO } from 'date-fns';
import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { QueryKey } from '@/Config/api/queryKey';
import { Usage } from '@/Domain/api/Subscription/dto/SubscriptionUsage.dto';
import { Card, CardContent, CardHeader, CardTitle } from '@/Domain/components/Shared/Card/Card';
import { buildAiCreditsFaqPath } from '@/Domain/components/Subscription/aiCreditsFaqLink';
import { getUsageBarColor, getUsageTextColor } from '@/Domain/components/TierPlanCard/usageColor';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';

type Props = {
  tier: TierEnum | undefined;
  nextPaymentOn?: string | null;
};

export const formatRenewalDate = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const parsed = parseISO(raw);
  return isNaN(parsed.getTime()) ? null : format(parsed, 'd MMMM, yyyy');
};

export const AICreditsUsageCard = ({ tier, nextPaymentOn }: Props) => {
  const { t } = useTranslation('subscription_page');
  const postHog = usePostHog();
  const navigate = useNavigate();
  const { data } = useQuery<Usage>({ queryKey: [QueryKey.subscription.usage] });

  const openAiCreditsFaq = () => {
    postHog?.capture(PostHogEventEnum.AiCreditsFaqLinkClicked, { source: 'billing_card' });
    navigate(buildAiCreditsFaqPath());
  };

  const messagesLimit = data?.messages_limit ?? 0;
  // Enterprise (CUSTOM) has no metered AI-credit limit, and a 0 limit means unlimited/unprovisioned;
  // in both cases a used/limit bar is meaningless (would read "N / 0 credits"), so hide the card.
  if (!data || tier === TierEnum.CUSTOM || messagesLimit <= 0) return null;

  const messagesUsed = data.messages_used ?? 0;
  const messagesRemaining = Math.max(0, messagesLimit - messagesUsed);
  const percentUsed = Math.min(100, (messagesUsed / messagesLimit) * 100);
  const topupCredits = data.topup_credits ?? 0;
  // Purchased credits keep a fully used monthly pool from displaying outage severity.
  const severityPercent = topupCredits > 0 ? Math.min(percentUsed, 99) : percentUsed;

  const renewalDate = formatRenewalDate(nextPaymentOn ?? data.messages_next_refreshed);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <button
            type="button"
            onClick={openAiCreditsFaq}
            className="cursor-pointer underline decoration-muted-foreground/50 decoration-dotted underline-offset-4 transition-colors hover:decoration-foreground"
            data-testid="ai-credits-usage-card-title-link"
          >
            {t('usage_card.title')}
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-end justify-between">
          <p className="text-sm text-foreground">
            <span className="text-2xl font-semibold">{messagesUsed.toLocaleString()}</span>
            <span className="text-muted-foreground">
              {' / '}
              {messagesLimit.toLocaleString()}{' '}
              <button
                type="button"
                onClick={openAiCreditsFaq}
                className="cursor-pointer underline decoration-muted-foreground/50 decoration-dotted underline-offset-2 transition-colors hover:decoration-foreground"
                data-testid="ai-credits-usage-card-credits-link"
              >
                {t('usage_card.credits')}
              </button>
            </span>
          </p>
          <span className="text-sm font-medium text-muted-foreground">
            {t('usage_card.remaining', { amount: messagesRemaining.toLocaleString() })}
          </span>
        </div>

        <div className="h-2 w-full bg-muted-foreground/15 rounded-full overflow-hidden">
          <div
            className={classNames(
              'h-full rounded-full transition-all duration-500 ease-out',
              getUsageBarColor(severityPercent),
            )}
            style={{ width: `${percentUsed}%` }}
          />
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className={classNames('font-medium', getUsageTextColor(severityPercent))}>
            {Math.round(percentUsed)}% {t('usage_card.used')}
          </span>
          {renewalDate && (
            <span className="text-muted-foreground">
              {t('usage_card.refreshes_on', { date: renewalDate })}
            </span>
          )}
        </div>

        {topupCredits > 0 && (
          <div
            className="flex items-center justify-between border-t border-border pt-3 text-sm"
            data-testid="ai-credits-usage-topup"
          >
            <span className="text-foreground">
              <span className="font-semibold">+{topupCredits.toLocaleString()}</span>{' '}
              <span className="text-muted-foreground">{t('usage_card.topup_extra')}</span>
            </span>
            <span className="text-muted-foreground">{t('usage_card.topup_no_expiry')}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
